import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import logo from '../Assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { announcementEntriesAPI, announcementAPI } from '../services/api';
import {
  DashHomeIcon,
  DashAllReviewersIcon,
  DashMyLibraryIcon,
  DashSettingsIcon,
  DashHelpCenterIcon,
} from './Icons';

function AnnouncementContent({ text }) {
  return (
    <p
      className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 py-[6px] font-inter text-[13px] text-[#0F172A] text-center [&_a]:font-semibold [&_a]:text-[#6E43B9] [&_a]:hover:underline"
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );
}

const TAB_CLASS =
  'flex items-center gap-2 font-inter font-semibold text-sm py-2 px-1 border-b-[3px] border-transparent transition-colors';
const TAB_ACTIVE = 'text-[#6E43B9] border-b-[#6E43B9]';
const TAB_INACTIVE = 'text-[#6C737F] hover:text-[#6E43B9]';

const TABS = [
  { to: '/dashboard', label: 'Dashboard', Icon: DashHomeIcon, end: true },
  { to: '/dashboard/all-reviewers', label: 'All Reviewers', Icon: DashAllReviewersIcon, end: true },
  { to: '/dashboard/library', label: 'My Library', Icon: DashMyLibraryIcon, end: true },
  { to: '/dashboard/settings', label: 'Account Settings', Icon: DashSettingsIcon, end: false },
  { to: '/dashboard/help', label: 'Help Center', Icon: DashHelpCenterIcon, end: true },
];

function DashNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isExamDetailsPage = location.pathname.startsWith('/dashboard/exam/');
  const fromLibrary = isExamDetailsPage && new URLSearchParams(location.search).get('from') === 'library';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [hasUnreadNews, setHasUnreadNews] = useState(false);
  const showUnreadDot = hasUnreadNews;
  const profileRef = useRef(null);
  const [announcement, setAnnouncement] = useState(null); // null = loading, { text, enabled } | '' when done
  const showBanner = location.pathname === '/dashboard' || location.pathname === '/dashboard/all-reviewers';

  useEffect(() => {
    let alive = true;
    announcementEntriesAPI
      .getUnread()
      .then((r) => {
        if (alive && r.success) setHasUnreadNews(!!r.hasUnread);
      })
      .catch(() => { });
    const onSeen = () => setHasUnreadNews(false);
    window.addEventListener('whatsnew:seen', onSeen);
    return () => {
      alive = false;
      window.removeEventListener('whatsnew:seen', onSeen);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    announcementAPI.get()
      .then((r) => { if (alive && r.success) setAnnouncement(r.data); })
      .catch(() => { if (alive) setAnnouncement({}); });
    return () => { alive = false; };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const initials = user
    ? `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase()
    : '??';

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  useEffect(() => {
    const fn = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('click', fn);
    return () => document.removeEventListener('click', fn);
  }, []);

  const getTabActive = (label, isActive) => {
    if (label === 'All Reviewers') return isActive || (isExamDetailsPage && !fromLibrary);
    if (label === 'My Library') return isActive || (isExamDetailsPage && fromLibrary);
    return isActive;
  };

  const renderTab = (tab, isDrawer = false) => {
    const { to, label, Icon, end } = tab;
    const linkClass = isDrawer ? 'block py-3 px-2 border-b border-transparent' : '';
    return (
      <NavLink
        key={to}
        to={to}
        end={end}
        onClick={isDrawer ? () => setDrawerOpen(false) : undefined}
        className={({ isActive }) => {
          const active = getTabActive(label, isActive);
          const base = isDrawer ? `${TAB_CLASS} ${linkClass}` : TAB_CLASS;
          return `${base} ${active ? TAB_ACTIVE : TAB_INACTIVE}`;
        }}
      >
        {() => (
          <>
            <Icon className="w-5 h-5 shrink-0" />
            <span>{label}</span>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <>
      <nav className="bg-white">
        <div className="max-w-[1440px] mx-auto">
          {/* Top section: logo (left), burger on mobile / profile on desktop (right) */}
          <div className="flex items-center justify-between py-4 px-4 sm:px-6 lg:px-20 border-b border-[#F2F4F7]">
            <NavLink to="/dashboard/all-reviewers" className="flex items-center">
              <img src={logo} alt="Reviewly" className="h-9 w-auto object-contain" />
            </NavLink>
            {/* Burger - mobile only (replaces profile in top bar) */}
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-2 rounded-lg text-[#6C737F] hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* Announcement + Profile - desktop only (mobile: shown inside drawer) */}
            <div className="hidden md:flex items-center gap-6">
              <NavLink
                to="/dashboard/announcements"
                className="flex items-center gap-[8px] font-inter font-[800] text-[14px] transition-colors text-[#6E43B9]"
              >
                <span
                  className={`w-[8px] h-[8px] rounded-full ${showUnreadDot ? 'bg-[#FFC92A]' : 'bg-transparent'}`}
                  aria-hidden="true"
                />
                <span>Announcements</span>
              </NavLink>
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  aria-label="Profile"
                  aria-expanded={profileOpen}
                  onClick={() => setProfileOpen((o) => !o)}
                  className="w-10 h-10 rounded-full border-2 border-[#6E43B9]/30 bg-gray-100 flex items-center justify-center overflow-hidden shrink-0 hover:ring-2 hover:ring-[#6E43B9]/20 transition-shadow"
                >
                  {user?.profilePic ? (
                    <img src={user.profilePic} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="text-sm font-semibold text-[#6E43B9]">{initials}</span>
                  )}
                </button>
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 py-2 bg-white rounded-lg shadow-lg border border-[#E5E7EB] z-50">
                    <p className="px-4 py-2 font-inter text-sm text-[#6C737F] truncate" title={user?.email}>
                      {user?.email || '—'}
                    </p>
                    <button
                      type="button"
                      onClick={() => { setProfileOpen(false); handleLogout(); }}
                      className="w-full px-4 py-2 font-inter font-medium text-sm text-red-600 hover:bg-red-50 text-left transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom tabs - desktop only */}
          <div className="hidden md:flex items-center gap-2 py-2 px-4 sm:px-6 lg:px-20">
            {TABS.map((tab) => renderTab(tab, false))}
          </div>
        </div>
      </nav>

      {showBanner && announcement?.enabled && announcement?.text && (
        <div className="md:sticky md:top-0 md:z-30 w-full bg-[#FFF6DC] border-b border-[#FFE082] animate-[fadeIn_0.3s_ease]">
          <AnnouncementContent text={announcement.text} />
        </div>
      )}

      {/* Backdrop - mobile drawer overlay */}
      <div
        role="presentation"
        onClick={() => setDrawerOpen(false)}
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ease-out md:hidden ${drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        aria-hidden="true"
      />

      {/* Drawer - slides in from right */}
      <div
        className={`fixed top-0 right-0 h-full w-[280px] max-w-[85vw] z-50 bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out md:hidden ${drawerOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        aria-modal="true"
        aria-label="Dashboard navigation"
      >
        <div className="flex justify-between items-center h-14 px-6 border-b border-[#F2F4F7]">
          <span className="text-lg font-semibold text-[#0F172A]">Menu</span>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="p-2 rounded-lg text-[#6C737F] hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="flex flex-col p-4 gap-0 flex-1">
          {TABS.map((tab) => renderTab(tab, true))}
        </nav>
        <div className="mt-auto">
          <NavLink
            to="/dashboard/announcements"
            onClick={() => setDrawerOpen(false)}
            className="flex items-center gap-[8px] px-2 py-2 mb-3 font-inter font-[800] text-[14px] text-[#6E43B9]"
          >
            <span
              className={`w-[8px] h-[8px] rounded-full shrink-0 ${showUnreadDot ? 'bg-[#FFC92A]' : 'bg-transparent'}`}
              aria-hidden="true"
            />
            <span>Announcement</span>
          </NavLink>
          <div className="flex items-center gap-3 mb-3 border-t border-[#F2F4F7] p-4">
            <div className="w-10 h-10 rounded-full border-2 border-[#6E43B9]/30 bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
              {user?.profilePic ? (
                <img src={user.profilePic} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <span className="text-sm font-semibold text-[#6E43B9]">{initials}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-inter text-sm font-semibold text-[#0F172A] truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="font-inter text-xs text-[#6C737F] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setDrawerOpen(false); handleLogout(); }}
            className="w-full font-inter font-medium text-sm text-red-600 hover:bg-red-50 py-2 border border-red-200 rounded-lg transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    </>
  );
}

export default DashNav;
